import { useState } from 'react';
import { useAuth } from '../App';
import { API } from '../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function AuthModal({ isOpen, onClose, defaultMode = 'login' }) {
  const [mode, setMode] = useState(defaultMode);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password,
        });
        login(response.data.token, response.data.user);
        toast.success('Logged in successfully!');
        onClose();
        if (response.data.user.role === 'business_owner') {
          navigate('/dashboard');
        } else if (response.data.user.role === 'admin') {
          navigate('/admin');
        }
      } else {
        const response = await axios.post(`${API}/auth/register`, formData);
        login(response.data.token, response.data.user);
        toast.success('Account created successfully!');
        onClose();
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'signup' && (
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required={mode === 'signup'}
                data-testid="name-input"
                className="h-12"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              data-testid="email-input"
              className="h-12"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
              data-testid="password-input"
              className="h-12"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-12 rounded-full bg-primary hover:bg-primary/90 text-white font-medium"
            disabled={loading}
            data-testid="auth-submit-button"
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Login' : 'Sign Up'}
          </Button>
        </form>

        <div className="mt-4 text-center text-sm">
          {mode === 'login' ? (
            <p>
              Don't have an account?{' '}
              <button
                onClick={() => setMode('signup')}
                className="text-primary font-medium hover:underline"
                data-testid="switch-to-signup"
              >
                Sign up
              </button>
            </p>
          ) : (
            <p>
              Already have an account?{' '}
              <button
                onClick={() => setMode('login')}
                className="text-primary font-medium hover:underline"
                data-testid="switch-to-login"
              >
                Login
              </button>
            </p>
          )}
        </div>

        {mode === 'login' && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg text-sm text-slate-600">
            <p className="font-medium mb-2">Demo Credentials:</p>
            <p>Admin: admin@nearconnect.com / admin123</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
